import { Grid, Stack, Typography } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { OnboardingStepper } from "../components/OnboardingStepper";
import { SliderWithInput } from "../components/SliderWithInput";

import { useLocalTargetState } from "../util/useLocalTargetState.ts";
import { Target } from "@labs/shared/types/Target.ts";
import { analyzePlan } from "../util/planAnalysis/analyzePlan.ts";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useLocalSolutionMapState } from "../util/useLocalSolutionMapState.ts";
import { useLocalBaselineEmissionsState } from "../util/useLocalBaselineEmissionsState.ts";
import { onboardingSteps } from "../data/onboardingSteps.ts";
import { LoadingIndicator } from "../components/LoadingIndicator.tsx";
import { trackEvent } from "../util/analytics";
import { EventCategory } from "@labs/shared/types/EventCategory.ts";
import { HeroHeader } from "../components/HeroHeader.tsx";
import { LabsCard } from "../components/LabsCard.tsx";
import { NavigationButtons } from "../components/NavigationButtons.tsx";
import { YearPicker } from "../components/YearPicker";
import targetGoalImg from "../assets/banners/targetGoal.png";

const ForecastLineChart = lazy(() => import("../charts/ForecastLineChart"));

const TargetForm = () => {
  const [target, setTarget] = useLocalTargetState();
  const [solutionMap] = useLocalSolutionMapState();
  const [baselineEmissions] = useLocalBaselineEmissionsState();
  const { control, handleSubmit } = useForm<Target>({
    mode: "onChange",
    defaultValues: target,
  });
  const targetFormValues = useWatch({ control });
  const analyzedPlan = useMemo(
    () => analyzePlan(solutionMap, targetFormValues, baselineEmissions),
    [baselineEmissions, targetFormValues, solutionMap],
  );
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent(EventCategory.Target, "Start");
  }, []);

  const [hoveredCharts] = useState(new Set<string>());

  const onSubmit = (data: Target) => {
    trackEvent(EventCategory.Target, "Complete");
    setTarget(data);
    navigate("/baseline");
  };

  return (
    <Stack direction={"column"} spacing={4}>
      <OnboardingStepper activeStep={0} steps={onboardingSteps} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={4}>
          <Grid size={12}>
            <HeroHeader
              title={"Decarbonization Target"}
              description={
                "Define your short-term decarbonization target. " +
                "If you have a longer-term net zero target and a shorter-term " +
                "milestone, choose the milestone target."
              }
              backgroundImage={targetGoalImg}
              showCalculateLink
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <LabsCard>
              <Stack direction="column" spacing={4}>
                <Typography variant={"h6"} color={"primary.dark"}>
                  Set a Target
                </Typography>
                <Typography variant={"body1"}>
                  We recommend starting with a 42% absolute Scope 1 and 2
                  reduction by 2030 in accordance with the Science Based Target
                  Initiative.
                </Typography>
                <Controller
                  name="baselineYear"
                  control={control}
                  defaultValue="2020"
                  render={({ field }) => (
                    <YearPicker
                      label="Base Year"
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        trackEvent(
                          EventCategory.Target,
                          "Change Field Value",
                          field.name,
                          value,
                        );
                      }}
                      minYear={2015}
                      maxYear={2025}
                      helperText="Reference year for your reduction measurements"
                    />
                  )}
                />
                <Controller
                  name="targetYear"
                  control={control}
                  defaultValue="2030"
                  render={({ field }) => (
                    <YearPicker
                      label="Target Year"
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        trackEvent(
                          EventCategory.Target,
                          "Change Field Value",
                          field.name,
                          value,
                        );
                      }}
                      minYear={2026}
                      maxYear={2050}
                      helperText="Year you will achieve this reduction target"
                    />
                  )}
                />
                <SliderWithInput
                  name="goal"
                  label="Target Reduction"
                  helperText="Absolute Scope 1 and 2 emissions reduction"
                  control={control}
                  min={0}
                  max={100}
                  step={1}
                />
                <SliderWithInput
                  name="expectedBusinessGrowth"
                  label="Business Growth"
                  helperText="As your business grows so will energy demands. We represent your base year as 100 units of carbon and scale those units 1:1 with business growth"
                  control={control}
                  min={0}
                  max={50}
                  step={1}
                />
              </Stack>
            </LabsCard>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <LabsCard>
              <Typography variant={"h6"} color={"primary.dark"}>
                Emissions Forecast
              </Typography>
              <Suspense fallback={<LoadingIndicator />}>
                <ForecastLineChart
                  target={targetFormValues}
                  analyzedPlan={analyzedPlan}
                  showEditLink={false}
                  hoveredCharts={hoveredCharts}
                  analyticsCategory={EventCategory.Target}
                />
              </Suspense>
            </LabsCard>
          </Grid>
          <Grid size={12}>
            <NavigationButtons backEnabled nextEnabled backHref="/" />
          </Grid>
        </Grid>
      </form>
    </Stack>
  );
};

export default TargetForm;
